/**
 * 坐标系转换：WGS84 → GCJ-02。
 *
 * 无人机遥测给出的是 WGS84（GPS 原始坐标），而高德底图使用 GCJ-02（国测局加密坐标），
 * 境内两者相差数百米，直接绘制会让标记与道路明显错位，因此送进地图前必须转换。
 *
 * 本模块只做**正向**转换，且**只应被地图边界层 `lib/map.ts` 调用**：
 * 遥测完整数据表、注册信息等界面继续展示 WGS84 原始值，那才是无人机的真实坐标。
 */

const PI = Math.PI;
/** 克拉索夫斯基椭球长半轴（米） */
const SEMI_MAJOR_AXIS = 6378245;
/** 第一偏心率平方 */
const ECCENTRICITY_SQUARED = 0.00669342162296594323;

/** 国境粗略范围。GCJ-02 只在中国大陆生效，境外必须保持原值。 */
export function isOutOfChina(lng: number, lat: number): boolean {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function rawLatOffset(x: number, y: number): number {
  let value = -100 + 2 * x + 3 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  value += ((20 * Math.sin(6 * x * PI) + 20 * Math.sin(2 * x * PI)) * 2) / 3;
  value += ((20 * Math.sin(y * PI) + 40 * Math.sin((y / 3) * PI)) * 2) / 3;
  value += ((160 * Math.sin((y / 12) * PI) + 320 * Math.sin((y * PI) / 30)) * 2) / 3;
  return value;
}

function rawLngOffset(x: number, y: number): number {
  let value = 300 + x + 2 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  value += ((20 * Math.sin(6 * x * PI) + 20 * Math.sin(2 * x * PI)) * 2) / 3;
  value += ((20 * Math.sin(x * PI) + 40 * Math.sin((x / 3) * PI)) * 2) / 3;
  value += ((150 * Math.sin((x / 12) * PI) + 300 * Math.sin((x / 30) * PI)) * 2) / 3;
  return value;
}

/**
 * WGS84 → GCJ-02，返回 `[lng, lat]`（与地图库的坐标顺序一致）。
 *
 * 纯函数、无依赖、无网络请求：同一输入永远得到同一输出。
 */
export function wgs84ToGcj02(lng: number, lat: number): [number, number] {
  if (isOutOfChina(lng, lat)) return [lng, lat];

  const x = lng - 105;
  const y = lat - 35;
  const radLat = (lat / 180) * PI;

  let magic = Math.sin(radLat);
  magic = 1 - ECCENTRICITY_SQUARED * magic * magic;
  const sqrtMagic = Math.sqrt(magic);

  const dLat =
    (rawLatOffset(x, y) * 180) /
    (((SEMI_MAJOR_AXIS * (1 - ECCENTRICITY_SQUARED)) / (magic * sqrtMagic)) * PI);
  const dLng = (rawLngOffset(x, y) * 180) / ((SEMI_MAJOR_AXIS / sqrtMagic) * Math.cos(radLat) * PI);

  return [lng + dLng, lat + dLat];
}

export type Wgs84Path = readonly (readonly [number, number])[];
export type Gcj02Path = [number, number][];

/**
 * 航迹坐标转换器（每台无人机一个实例）。
 *
 * 航迹最多 600 个落点、总览页还有 6 台机同时刷新，逐帧整条重算没有必要：
 * 转换结果是单点的纯函数，历史落点不会因为出现新落点而改变，所以只补算新增部分。
 *
 * `trail.ts` 的特点是**原地追加**，满 600 点后从头部 `splice`，因此分三种情况：
 * - 追加：只转换新尾部（均摊 O(1)）；
 * - 头部截断：丢掉已转换的前缀，只转换新尾部；
 * - 换了数组（换机、清空后会拿到新数组）：整条重算。
 *
 * 变化检测一律用点对象的**引用相等**——航迹里的每个点都是 `recordTrailPoint` 新建的数组，
 * 引用不会重复，因此不会因为坐标值恰好相同而误判。
 */
export function createTrailConverter(): (path: Wgs84Path) => Gcj02Path {
  /** 上一次转换的数组**身份**，用来区分「原地更新」与「换了数组」 */
  let sourceRef: Wgs84Path | null = null;
  /**
   * 已转换点的**快照**（同序、与 `converted` 一一对应）。
   * 必须是真的复制一份：`source === path` 时逐点比较等于自己跟自己比，恒为真，
   * 头部截断就永远检测不出来（返回陈旧坐标，整条航迹错位）。
   */
  let source: readonly (readonly [number, number])[] = [];
  let converted: Gcj02Path = [];

  const convertFrom = (path: Wgs84Path, from: number): void => {
    for (let i = from; i < path.length; i += 1) {
      const point = path[i]!;
      converted.push(wgs84ToGcj02(point[0], point[1]));
    }
  };

  const rebuild = (path: Wgs84Path): Gcj02Path => {
    sourceRef = path;
    source = path.slice();
    converted = [];
    convertFrom(path, 0);
    return converted;
  };

  return (path) => {
    if (path !== sourceRef || path.length < converted.length) return rebuild(path);

    if (path.length > converted.length) {
      // 原地追加：历史落点没有被改动，只补算新增的尾部
      source = path.slice();
      convertFrom(path, converted.length);
      return converted;
    }

    if (converted.length === 0) return converted;

    if (path[0] === source[0] && path[converted.length - 1] === source[converted.length - 1]) {
      return converted;
    }

    // 长度不变但内容变了：只可能是头部被截断，找回被截掉了几个点
    let drop = 0;
    while (drop < source.length && source[drop] !== path[0]) drop += 1;
    // drop === 0 说明只是尾部被改写，不是截断，只能整条重算
    if (drop === 0 || drop >= source.length) return rebuild(path);

    converted = converted.slice(drop);
    source = path.slice();
    convertFrom(path, converted.length);
    return converted;
  };
}
